import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { historicalScenarios } from '../data/comparisonData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiActivity, FiDollarSign, FiCalendar, FiPercent, FiShield, FiArrowRight, FiInfo, FiTarget, FiAlertCircle } = FiIcons;

const UnifiedInvestmentTools = () => {
  const [activeTab, setActiveTab] = useState('projection');
  const [initialValue, setInitialValue] = useState(100000);
  const [goldAlloc, setGoldAlloc] = useState(15);
  const [retirementAge, setRetirementAge] = useState(65);
  const [currentAge, setCurrentAge] = useState(50);
  const [annualContribution, setAnnualContribution] = useState(6000);
  const [activeScenarioId, setActiveScenarioId] = useState(historicalScenarios[0].id);
  const [bondAlloc, setBondAlloc] = useState(25);
  
  const stockAlloc = 100 - goldAlloc - (activeTab === 'stress-test' ? bondAlloc : 0);
  const AFFILIATE_LINK = "https://learn.augustapreciousmetals.com/apm-aff-lp-1-v3?apmtrkr_cid=1696&aff_id=5283&sub_id=YOUR_IDENTIFIER";

  const projectionResults = useMemo(() => {
    const years = Math.max(1, retirementAge - currentAge);
    const expectedStockReturn = 0.07;
    const expectedGoldReturn = 0.05;
    
    let standardBalance = initialValue;
    let diversifiedStocks = initialValue * (1 - (goldAlloc / 100));
    let diversifiedGold = initialValue * (goldAlloc / 100);
    
    const chartData = { years: [], standard: [], diversified: [] };

    for (let i = 0; i <= years; i++) {
      chartData.years.push(`Age ${currentAge + i}`);
      chartData.standard.push(Math.round(standardBalance));
      chartData.diversified.push(Math.round(diversifiedStocks + diversifiedGold));
      
      if (i < years) {
        standardBalance = (standardBalance + annualContribution) * (1 + expectedStockReturn);
        const stockContrib = annualContribution * (1 - (goldAlloc / 100));
        const goldContrib = annualContribution * (goldAlloc / 100);
        diversifiedStocks = (diversifiedStocks + stockContrib) * (1 + expectedStockReturn);
        diversifiedGold = (diversifiedGold + goldContrib) * (1 + expectedGoldReturn);
      }
    }
    return { 
      chartData, 
      finalStandard: Math.round(standardBalance), 
      finalDiversified: Math.round(diversifiedStocks + diversifiedGold),
      finalGoldValue: Math.round(diversifiedGold)
    };
  }, [initialValue, goldAlloc, retirementAge, currentAge, annualContribution]);

  const activeScenario = useMemo(() => 
    historicalScenarios.find(s => s.id === activeScenarioId) || historicalScenarios[0]
  , [activeScenarioId]);

  const stressResults = useMemo(() => {
    const { stocks, bonds, gold } = activeScenario.data;
    const allStockValue = Math.round(initialValue + (initialValue * (stocks / 100)));
    const tradValue = Math.round((initialValue * 0.6 * (1 + stocks/100)) + (initialValue * 0.4 * (1 + bonds/100)));
    const protectedValue = Math.round(
      (initialValue * (stockAlloc/100) * (1 + stocks/100)) + 
      (initialValue * (bondAlloc/100) * (1 + bonds/100)) + 
      (initialValue * (goldAlloc/100) * (1 + gold/100))
    );
    const mitigation = ((protectedValue - allStockValue) / initialValue * 100).toFixed(1);
    return { allStockValue, tradValue, protectedValue, mitigation };
  }, [initialValue, activeScenario, goldAlloc, bondAlloc, stockAlloc]);

  const projectionOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['Standard Portfolio', `Diversified (${goldAlloc}% Gold)`], bottom: 0 },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
    xAxis: { type: 'category', data: projectionResults.chartData.years },
    yAxis: { type: 'value', axisLabel: { formatter: (v) => `$${(v / 1000).toFixed(0)}k` } },
    series: [
      { name: 'Standard Portfolio', type: 'line', data: projectionResults.chartData.standard, itemStyle: { color: '#94a3b8' }, lineStyle: { type: 'dashed' }, smooth: true },
      { name: `Diversified (${goldAlloc}% Gold)`, type: 'line', data: projectionResults.chartData.diversified, itemStyle: { color: '#eab308' }, areaStyle: { color: 'rgba(234,179,8,0.1)' }, smooth: true }
    ]
  };

  const stressOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '4%', bottom: '10%', top: '15%', containLabel: true },
    xAxis: { type: 'category', data: ['100% Stocks', '60/40 Split', 'Protected Portfolio'], axisLabel: { fontWeight: 'bold' } },
    yAxis: { type: 'value', axisLabel: { formatter: (v) => `$${(v / 1000).toFixed(0)}k` } },
    series: [{
      type: 'bar', barWidth: '50%',
      data: [
        { value: stressResults.allStockValue, itemStyle: { color: '#94a3b8' } },
        { value: stressResults.tradValue, itemStyle: { color: '#3b82f6' } },
        { value: stressResults.protectedValue, itemStyle: { color: '#eab308' } }
      ],
      label: { show: true, position: 'top', formatter: (p) => `$${p.value.toLocaleString()}` }
    }]
  };

  return (
    <section id="gold-ira-calculator" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 font-serif">Advanced Gold IRA Calculator</h2>
          <div className="inline-flex p-1 bg-slate-100 rounded-2xl border border-slate-200 shadow-inner">
            <button onClick={() => setActiveTab('projection')} className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${activeTab === 'projection' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500'}`}>
              <SafeIcon icon={FiTrendingUp} /> Future Projection
            </button>
            <button onClick={() => setActiveTab('stress-test')} className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${activeTab === 'stress-test' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500'}`}>
              <SafeIcon icon={FiActivity} /> Historical Stress Test
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center gap-2"> <SafeIcon icon={FiTarget} className="text-yellow-500" /> Calculator Inputs </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Initial Investment ($)</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"> <SafeIcon icon={FiDollarSign} className="text-slate-400" /> </div>
                    <input type="number" value={initialValue} onChange={(e) => setInitialValue(Number(e.target.value))} className="w-full pl-10 pr-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-yellow-500 font-bold" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Gold Allocation: {goldAlloc}%</label>
                  <input type="range" min="0" max="50" step="1" value={goldAlloc} onChange={(e) => setGoldAlloc(Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-yellow-500" />
                </div>
                {activeTab === 'projection' ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Current Age</label>
                        <input type="number" value={currentAge} onChange={(e) => setCurrentAge(Number(e.target.value))} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Retirement Age</label>
                        <input type="number" value={retirementAge} onChange={(e) => setRetirementAge(Number(e.target.value))} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Annual Add-on ($)</label>
                      <input type="number" value={annualContribution} onChange={(e) => setAnnualContribution(Number(e.target.value))} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Bond Allocation: {bondAlloc}%</label>
                      <input type="range" min="0" max="60" step="1" value={bondAlloc} onChange={(e) => setBondAlloc(Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-500" />
                    </div>
                    <div className="space-y-2 mt-4">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Select Scenario</label>
                      {historicalScenarios.map(s => (
                        <button key={s.id} onClick={() => setActiveScenarioId(s.id)} className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${activeScenarioId === s.id ? 'bg-slate-900 text-white font-bold border-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-8 flex flex-col">
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl flex-grow mb-8 overflow-hidden">
              <div className="h-[400px]"> <ReactECharts option={activeTab === 'projection' ? projectionOption : stressOption} style={{ height: '100%', width: '100%' }} /> </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-yellow-500 p-6 rounded-3xl text-slate-900 shadow-lg">
                <SafeIcon icon={FiShield} className="text-2xl mb-3" />
                <div className="text-xs font-bold uppercase opacity-80">{activeTab === 'projection' ? 'Projected Gold Value' : 'Portfolio Savings'}</div>
                <div className="text-2xl font-bold mt-1">${activeTab === 'projection' ? projectionResults.finalGoldValue.toLocaleString() : (stressResults.protectedValue - stressResults.allStockValue).toLocaleString()}</div>
              </div>
              <div className="bg-slate-900 p-6 rounded-3xl text-white shadow-lg">
                <SafeIcon icon={FiTrendingUp} className="text-2xl mb-3 text-yellow-400" />
                <div className="text-xs font-bold uppercase opacity-80">{activeTab === 'projection' ? 'Final Portfolio Value' : 'Protection Power'}</div>
                <div className="text-2xl font-bold mt-1">{activeTab === 'projection' ? `$${projectionResults.finalDiversified.toLocaleString()}` : `+${stressResults.mitigation}%`}</div>
              </div>
              <div className="bg-blue-600 p-6 rounded-3xl text-white shadow-lg md:col-span-2 lg:col-span-1">
                <div className="flex flex-col h-full justify-center">
                  <p className="text-xs font-bold mb-3">Qualified For Premium Kit?</p>
                  <a href={AFFILIATE_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-blue-600 px-4 py-2 rounded-xl text-sm font-bold hover:bg-blue-50 transition-colors"> Check Eligibility <SafeIcon icon={FiArrowRight} /> </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UnifiedInvestmentTools;